import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getLocations } from '../../shared/api/locations.js'
import type { Location } from '../../shared/types/location.js'
import { canManageCatalog } from '../../shared/utils/permissions.js'
import { ROUTES } from '../../app/routes.js'
import LocationCard from './components/LocationCard.js'
import EmptyState from '../menu/components/EmptyState.js'

type PageStatus = 'loading' | 'ready' | 'error'

export default function LocationsPage() {
  const { t } = useTranslation()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageCatalog(role)

  const [locations, setLocations] = useState<Location[]>([])
  const [status, setStatus] = useState<PageStatus>('loading')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  async function load() {
    if (!orgId) return
    setStatus('loading')
    try {
      const res = await getLocations(orgId)
      setLocations(res.data)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }

  useEffect(() => {
    void load()
  }, [orgId])

  const showSearch = search.length > 0

  function filteredLocations(): Location[] {
    let result = locations
    if (statusFilter) {
      result = result.filter(l => l.status === statusFilter)
    }
    if (showSearch) {
      const q = search.toLowerCase()
      result = result.filter(l =>
        l.name.toLowerCase().includes(q) ||
        (l.slug && l.slug.toLowerCase().includes(q)) ||
        (l.address && l.address.toLowerCase().includes(q)),
      )
    }
    return result
  }

  const displayed = filteredLocations()

  if (status === 'loading') {
    return (
      <section className="page-section">
        <h2>{t('app.locations')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (status === 'error') {
    return (
      <section className="page-section">
        <h2>{t('app.locations')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('locations.loadError')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h2>{t('app.locations')}</h2>
          <p className="page-section-description">{t('locations.listSubtitle')}</p>
        </div>
        {canManage && (
          <a href={ROUTES.LOCATIONS_NEW} className="btn btn-primary" role="button">
            {t('locations.create')}
          </a>
        )}
      </div>

      <div className="product-filters">
        <div className="form-field">
          <label htmlFor="locationSearch">{t('locations.search')}</label>
          <input
            id="locationSearch"
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            maxLength={100}
            autoComplete="off"
            placeholder={t('locations.search')}
          />
        </div>
        <div className="form-field">
          <label htmlFor="locationStatusFilter">{t('products.statusFilter')}</label>
          <select
            id="locationStatusFilter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">{t('products.statusAll')}</option>
            <option value="ACTIVE">{t('products.statusActive')}</option>
            <option value="SUSPENDED">{t('products.statusSuspended')}</option>
            <option value="ARCHIVED">{t('products.statusArchived')}</option>
          </select>
        </div>
      </div>

      {displayed.length === 0 ? (
        <EmptyState
          title={showSearch || statusFilter ? t('locations.noResults') : t('locations.empty')}
          hint={showSearch || statusFilter ? t('locations.noResultsHint') : t('locations.emptyHint')}
        />
      ) : (
        <div className="menu-grid">
          {displayed.map(loc => (
            <LocationCard
              key={loc.id}
              location={loc}
              detailTo={`${ROUTES.LOCATIONS}/${loc.id}`}
              editTo={canManage ? `${ROUTES.LOCATIONS}/${loc.id}/edit` : undefined}
            />
          ))}
        </div>
      )}
    </section>
  )
}